import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, X, Calendar as CalendarIcon } from "lucide-react";

const AssenzeManager = ({ assenze, onChange, noteTurni, onNoteTurniChange }) => {
  // Gestione periodi (malattia, ferie)
  const addPeriodo = (tipo) => {
    const newAssenze = { ...assenze };
    if (!newAssenze[tipo]) newAssenze[tipo] = [];
    newAssenze[tipo].push({ dal: '', al: '' });
    onChange(newAssenze);
  };

  const removePeriodo = (tipo, index) => {
    const newAssenze = { ...assenze };
    newAssenze[tipo].splice(index, 1);
    if (newAssenze[tipo].length === 0) delete newAssenze[tipo];
    onChange(newAssenze);
  };

  const updatePeriodo = (tipo, index, field, value) => {
    const newAssenze = { ...assenze };
    newAssenze[tipo][index][field] = value;
    onChange(newAssenze);
  };

  // Gestione giorni singoli (donazione sangue, festivo lavorato, riposo settimanale)
  const addGiorno = (tipo) => {
    const newAssenze = { ...assenze };
    if (!newAssenze[tipo]) newAssenze[tipo] = [];
    newAssenze[tipo].push('');
    onChange(newAssenze);
  };

  const removeGiorno = (tipo, index) => {
    const newAssenze = { ...assenze };
    newAssenze[tipo].splice(index, 1);
    if (newAssenze[tipo].length === 0) delete newAssenze[tipo];
    onChange(newAssenze);
  };

  const updateGiorno = (tipo, index, value) => {
    const newAssenze = { ...assenze };
    newAssenze[tipo][index] = value;
    onChange(newAssenze);
  };

  // Gestione ore (permessi, recupero, pronta disponibilità)
  const updateOre = (tipo, value) => {
    const newAssenze = { ...assenze };
    const numValue = parseFloat(value) || 0;
    if (numValue > 0) {
      newAssenze[tipo] = numValue;
    } else {
      delete newAssenze[tipo];
    }
    onChange(newAssenze);
  };

  return (
    <div className="space-y-6">
      {/* Periodi con date */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Malattia</CardTitle>
          <CardDescription className="text-xs">Indicare i giorni precisi (possono essere più periodi)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.malattia || []).map((periodo, idx) => (
            <div key={`malattia-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Label className="text-xs">Dal</Label>
                <Input
                  type="date"
                  value={periodo.dal}
                  onChange={(e) => updatePeriodo('malattia', idx, 'dal', e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex-1 min-w-0">
                <Label className="text-xs">Al</Label>
                <Input
                  type="date"
                  value={periodo.al}
                  onChange={(e) => updatePeriodo('malattia', idx, 'al', e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => removePeriodo('malattia', idx)}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => addPeriodo('malattia')}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Periodo Malattia
          </Button>
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Ferie</CardTitle>
          <CardDescription className="text-xs">Indicare i giorni precisi (possono essere più periodi)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.ferie || []).map((periodo, idx) => (
            <div key={`ferie-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Label className="text-xs">Dal</Label>
                <Input
                  type="date"
                  value={periodo.dal}
                  onChange={(e) => updatePeriodo('ferie', idx, 'dal', e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex-1 min-w-0">
                <Label className="text-xs">Al</Label>
                <Input
                  type="date"
                  value={periodo.al}
                  onChange={(e) => updatePeriodo('ferie', idx, 'al', e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => removePeriodo('ferie', idx)}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => addPeriodo('ferie')}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Periodo Ferie
          </Button>
        </CardContent>
      </Card>

      {/* Permessi con Ore e Date */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Permesso</CardTitle>
          <CardDescription className="text-xs">Indicare giorno e ore (possono essere più giorni)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.permesso_ore || []).map((item, idx) => (
            <div key={`permesso_ore-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={item.data || ''}
                  onChange={(e) => {
                    const newAssenze = { ...assenze };
                    if (!newAssenze.permesso_ore) newAssenze.permesso_ore = [];
                    newAssenze.permesso_ore[idx] = { ...item, data: e.target.value };
                    onChange(newAssenze);
                  }}
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-32 min-w-0">
                <Label className="text-xs">Ore</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={item.ore || 0}
                  onChange={(e) => {
                    const newAssenze = { ...assenze };
                    if (!newAssenze.permesso_ore) newAssenze.permesso_ore = [];
                    newAssenze.permesso_ore[idx] = { ...item, ore: parseFloat(e.target.value) || 0 };
                    onChange(newAssenze);
                  }}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  const newAssenze = { ...assenze };
                  newAssenze.permesso_ore.splice(idx, 1);
                  if (newAssenze.permesso_ore.length === 0) delete newAssenze.permesso_ore;
                  onChange(newAssenze);
                }}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => {
              const newAssenze = { ...assenze };
              if (!newAssenze.permesso_ore) newAssenze.permesso_ore = [];
              newAssenze.permesso_ore.push({ data: '', ore: 0 });
              onChange(newAssenze);
            }}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Permesso
          </Button>
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Permesso L104</CardTitle>
          <CardDescription className="text-xs">Indicare giorno e ore (possono essere più giorni)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.permesso_l104_ore || []).map((item, idx) => (
            <div key={`permesso_l104_ore-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={item.data || ''}
                  onChange={(e) => {
                    const newAssenze = { ...assenze };
                    if (!newAssenze.permesso_l104_ore) newAssenze.permesso_l104_ore = [];
                    newAssenze.permesso_l104_ore[idx] = { ...item, data: e.target.value };
                    onChange(newAssenze);
                  }}
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-32 min-w-0">
                <Label className="text-xs">Ore</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={item.ore || 0}
                  onChange={(e) => {
                    const newAssenze = { ...assenze };
                    if (!newAssenze.permesso_l104_ore) newAssenze.permesso_l104_ore = [];
                    newAssenze.permesso_l104_ore[idx] = { ...item, ore: parseFloat(e.target.value) || 0 };
                    onChange(newAssenze);
                  }}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  const newAssenze = { ...assenze };
                  newAssenze.permesso_l104_ore.splice(idx, 1);
                  if (newAssenze.permesso_l104_ore.length === 0) delete newAssenze.permesso_l104_ore;
                  onChange(newAssenze);
                }}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => {
              const newAssenze = { ...assenze };
              if (!newAssenze.permesso_l104_ore) newAssenze.permesso_l104_ore = [];
              newAssenze.permesso_l104_ore.push({ data: '', ore: 0 });
              onChange(newAssenze);
            }}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Permesso L104
          </Button>
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recupero Ore</CardTitle>
          <CardDescription className="text-xs">Indicare giorno e ore (possono essere più giorni)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.recupero_ore || []).map((item, idx) => (
            <div key={`recupero_ore-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={item.data || ''}
                  onChange={(e) => {
                    const newAssenze = { ...assenze };
                    if (!newAssenze.recupero_ore) newAssenze.recupero_ore = [];
                    newAssenze.recupero_ore[idx] = { ...item, data: e.target.value };
                    onChange(newAssenze);
                  }}
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-32 min-w-0">
                <Label className="text-xs">Ore</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={item.ore || 0}
                  onChange={(e) => {
                    const newAssenze = { ...assenze };
                    if (!newAssenze.recupero_ore) newAssenze.recupero_ore = [];
                    newAssenze.recupero_ore[idx] = { ...item, ore: parseFloat(e.target.value) || 0 };
                    onChange(newAssenze);
                  }}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  const newAssenze = { ...assenze };
                  newAssenze.recupero_ore.splice(idx, 1);
                  if (newAssenze.recupero_ore.length === 0) delete newAssenze.recupero_ore;
                  onChange(newAssenze);
                }}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => {
              const newAssenze = { ...assenze };
              if (!newAssenze.recupero_ore) newAssenze.recupero_ore = [];
              newAssenze.recupero_ore.push({ data: '', ore: 0 });
              onChange(newAssenze);
            }}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Recupero Ore
          </Button>
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Pronta Disponibilità</CardTitle>
          <CardDescription className="text-xs">Indicare giorno e ore (possono essere più giorni)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.pronta_disponibilita_ore || []).map((item, idx) => (
            <div key={`pronta_disponibilita_ore-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={item.data || ''}
                  onChange={(e) => {
                    const newAssenze = { ...assenze };
                    if (!newAssenze.pronta_disponibilita_ore) newAssenze.pronta_disponibilita_ore = [];
                    newAssenze.pronta_disponibilita_ore[idx] = { ...item, data: e.target.value };
                    onChange(newAssenze);
                  }}
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-32 min-w-0">
                <Label className="text-xs">Ore</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={item.ore || 0}
                  onChange={(e) => {
                    const newAssenze = { ...assenze };
                    if (!newAssenze.pronta_disponibilita_ore) newAssenze.pronta_disponibilita_ore = [];
                    newAssenze.pronta_disponibilita_ore[idx] = { ...item, ore: parseFloat(e.target.value) || 0 };
                    onChange(newAssenze);
                  }}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  const newAssenze = { ...assenze };
                  newAssenze.pronta_disponibilita_ore.splice(idx, 1);
                  if (newAssenze.pronta_disponibilita_ore.length === 0) delete newAssenze.pronta_disponibilita_ore;
                  onChange(newAssenze);
                }}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => {
              const newAssenze = { ...assenze };
              if (!newAssenze.pronta_disponibilita_ore) newAssenze.pronta_disponibilita_ore = [];
              newAssenze.pronta_disponibilita_ore.push({ data: '', ore: 0 });
              onChange(newAssenze);
            }}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Pronta Disponibilità
          </Button>
        </CardContent>
      </Card>

      {/* Giorni singoli */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Donazione Sangue</CardTitle>
          <CardDescription className="text-xs">Indicare il giorno preciso</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.donazione_sangue || []).map((giorno, idx) => (
            <div key={`donazione_sangue-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Input
                  type="date"
                  value={giorno}
                  onChange={(e) => updateGiorno('donazione_sangue', idx, e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => removeGiorno('donazione_sangue', idx)}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => addGiorno('donazione_sangue')}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Giorno
          </Button>
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Festivo Lavorato</CardTitle>
          <CardDescription className="text-xs">Giorni festivi in cui si è lavorato (possono essere più giorni)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.festivo_lavorato || []).map((giorno, idx) => (
            <div key={`festivo_lavorato-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Input
                  type="date"
                  value={giorno}
                  onChange={(e) => updateGiorno('festivo_lavorato', idx, e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => removeGiorno('festivo_lavorato', idx)}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => addGiorno('festivo_lavorato')}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Giorno
          </Button>
        </CardContent>
      </Card>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Riposo Settimanale</CardTitle>
          <CardDescription className="text-xs">Giorni di riposo settimanale (possono essere più giorni)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(assenze.riposo_settimanale || []).map((giorno, idx) => (
            <div key={`riposo_settimanale-${idx}-${periodo.dal || ""}`} className="flex flex-col sm:flex-row gap-2 sm:items-end">
              <div className="flex-1 min-w-0">
                <Input
                  type="date"
                  value={giorno}
                  onChange={(e) => updateGiorno('riposo_settimanale', idx, e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                onClick={() => removeGiorno('riposo_settimanale', idx)}
                variant="outline"
                size="sm"
                className="text-red-600 w-full sm:w-auto shrink-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() => addGiorno('riposo_settimanale')}
            variant="outline"
            size="sm"
            className="w-full gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Giorno
          </Button>
        </CardContent>
      </Card>

      {/* Note turni aggiuntivi */}
      <div className="space-y-2">
        <Label>Note (Eventuali Turni Aggiuntivi)</Label>
        <Textarea
          value={noteTurni}
          onChange={(e) => onNoteTurniChange(e.target.value)}
          placeholder="Inserisci eventuali note sui turni aggiuntivi..."
          rows={3}
        />
      </div>
    </div>
  );
};

export default AssenzeManager;
